const editableSelector =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

const nestedInteractionSelector = [
  '[role="dialog"]:not([class*="MuiDrawer"])',
  '.modal:not([class*="MuiDrawer"])',
  '[data-modal]:not([class*="MuiDrawer"])',
  '[role="listbox"]',
  '[role="menu"]',
  '[aria-haspopup]:not([aria-haspopup="false"])[aria-expanded="true"]',
  '[class~="fixed"][class*="z-50"]',
].join(", ");

const isElementVisible = (element: Element): boolean => {
  const htmlElement = element as HTMLElement;
  if (
    htmlElement.hidden ||
    element.getAttribute("aria-hidden") === "true" ||
    element.getClientRects().length === 0
  ) {
    return false;
  }
  const style = element.ownerDocument.defaultView?.getComputedStyle(element);
  return style?.display !== "none" && style?.visibility !== "hidden";
};

// An editable owns its Escape (a rename input, the pane's own textarea) —
// except one that opts out: the chat composer passes Escape through so the
// keystroke closes the open pane instead of doing nothing (#11819).
const isEditable = (target: EventTarget | null): boolean => {
  const element = target as Element | null;
  if (typeof element?.closest !== "function") return false;
  if (element.closest("[data-viewer-escape-passthrough]") !== null)
    return false;
  return element.closest(editableSelector) !== null;
};

export const hasOpenNestedInteraction = (doc: Document): boolean =>
  Array.from(doc.querySelectorAll(nestedInteractionSelector)).some(
    (element) =>
      isElementVisible(element) &&
      !element.closest('[class*="MuiDrawer"]') &&
      !element.closest("[data-document-drawer]")
  );

const nestedEscapeEvents = new WeakSet<KeyboardEvent>();

/** Remember nested precedence before target handlers can unmount their UI. */
export const captureDocumentViewerEscapePrecedence = (
  event: KeyboardEvent,
  doc: Document
): void => {
  if (
    event.key === "Escape" &&
    (isEditable(event.target) || hasOpenNestedInteraction(doc))
  ) {
    nestedEscapeEvents.add(event);
  }
};

export const shouldHandleDocumentViewerEscape = (
  event: KeyboardEvent,
  doc: Document
): boolean =>
  event.key === "Escape" &&
  !event.defaultPrevented &&
  !nestedEscapeEvents.has(event) &&
  !isEditable(event.target) &&
  !hasOpenNestedInteraction(doc);

/**
 * Forward Escape from the viewer-owned iframe after it has loaded.
 *
 * Keyboard events do not cross an iframe boundary. Installing on load avoids
 * the old race where FileViewer scanned the page once before PdfReader mounted.
 */
export const listenForViewerIframeEscape = (
  iframe: HTMLIFrameElement,
  onEscape: () => void
): (() => void) => {
  const frameDocument = iframe.contentDocument;
  const frameWindow = iframe.contentWindow;
  if (!frameDocument || !frameWindow) return () => undefined;

  const handleKeyDown = (event: KeyboardEvent) => {
    // PDF.js stops some keyboard events before they reach document bubble.
    // Capture at its window, but snapshot nested-interaction precedence now and
    // wait until propagation completes before honoring preventDefault().
    if (!shouldHandleDocumentViewerEscape(event, frameDocument)) return;
    queueMicrotask(() => {
      if (!event.defaultPrevented) onEscape();
    });
  };

  frameWindow.addEventListener("keydown", handleKeyDown, true);
  return () => {
    // The frame may have navigated to a page we cannot touch; the listener
    // went away with the old page (#14962). On unmount the frame is already
    // detached, so contentDocument is null and the skip is right there too.
    if (iframe.contentDocument !== frameDocument) return;
    frameWindow.removeEventListener("keydown", handleKeyDown, true);
  };
};
